import type { TaskWithCard } from '@web/data/repositories';
import { t } from '@web/i18n';
import { AppIcons, Card } from '@web/ui/core/atoms';
import { initials } from '@web/ui/core/utils';

import { TaskCard } from './task_card';

export interface MyTasksHeroProps {
  /** Tasks currently belonging to the viewing user (running + pending). */
  tasks: readonly TaskWithCard[];
  /** Pretty name for the "you" pill. */
  userName: string;
  onSelect: (task: TaskWithCard) => void;
}

/**
 * "Minhas tarefas" hero block — mirrors `.kv-tasks-mine` from the
 * mockup. Brand-tinted strip with a circle eyebrow + serif title and
 * a `you` pill on the right, followed by a responsive grid of
 * full-width task cards.
 */
export function MyTasksHero({
  tasks,
  userName,
  onSelect,
}: Readonly<MyTasksHeroProps>): React.JSX.Element | null {
  if (tasks.length === 0) return null;

  const userInitials = initials(userName);

  return (
    <Card tone="brand" surface="brand" data-testid="my-tasks-hero">
      <header className="flex items-center gap-3.5 pt-3 px-4">
        <span className="inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-pill bg-primary text-primary-foreground">
          <AppIcons name="user" size={18} />
        </span>
        <div className="flex min-w-0 flex-1 flex-col">
          <span className="font-serif text-section-title font-medium leading-card-title tracking-display-tight text-foreground">
            {t('workspace.myTasks.title')}
          </span>
          <span className="text-meta text-muted-foreground">
            {t('workspace.myTasks.sub', { count: tasks.length })}
          </span>
        </div>
        <span className="inline-flex items-center gap-1.5 rounded-pill border border-primary/22 bg-paper py-1 pl-1 pr-2.5">
          <span className="inline-flex h-5.5 w-5.5 items-center justify-center rounded-pill bg-primary font-mono text-mono-xs font-medium text-primary-foreground">
            {userInitials}
          </span>
          <span className="text-meta font-medium text-foreground">{userName}</span>
        </span>
      </header>

      <div className="grid grid-cols-mine-cards gap-2.5 py-3 px-4">
        {tasks.map((task) => (
          <TaskCard
            key={task.id}
            task={task}
            onClick={() => {
              onSelect(task);
            }}
          />
        ))}
      </div>
    </Card>
  );
}
